import { useEffect, useId, useState } from 'react';
import { ACCENT, GRID, GROUND, INK, INK_MUTED, SURFACE, SURFACE_MUTED } from './palette';

/**
 * Renders a Mermaid flowchart in the site's graphite theme. Mermaid is imported on demand, so it only loads on the pages
 * that draw a diagram. `chart` holds nodes and edges only; the direction (left-to-right on desktop, top-to-bottom on
 * phones) and the `machine` / `accent` node classes are added here.
 */

const CLASSES = `
  classDef machine fill:${SURFACE},stroke:${GRID},stroke-width:1px,color:${INK}
  classDef accent fill:${SURFACE_MUTED},stroke:${ACCENT},stroke-width:1.5px,color:${INK}
  linkStyle default stroke:${INK_MUTED},stroke-width:1.25px`;

const WIDE = '(min-width: 768px)';

let initialised = false;

interface MermaidDiagramProps {
  chart: string;
  /** Screen-reader description of what the diagram shows. */
  label: string;
}

export function MermaidDiagram({ chart, label }: MermaidDiagramProps) {
  const id = `mmd-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    import('mermaid')
      .then(async ({ default: mermaid }) => {
        if (!initialised) {
          mermaid.initialize({
            startOnLoad: false,
            securityLevel: 'strict',
            theme: 'base',
            fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
            themeVariables: {
              background: GROUND,
              primaryColor: SURFACE,
              primaryTextColor: INK,
              primaryBorderColor: GRID,
              lineColor: INK_MUTED,
              textColor: INK,
              edgeLabelBackground: GROUND,
              fontSize: '13px',
            },
            flowchart: { curve: 'basis', padding: 14, nodeSpacing: 36, rankSpacing: 64, htmlLabels: true },
          });
          initialised = true;
        }
        const { svg: rendered } = await mermaid.render(`${id}-${wide ? 'lr' : 'tb'}`, `flowchart ${wide ? 'LR' : 'TB'}${chart}\n${CLASSES}`);
        if (!cancelled) setSvg(rendered);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [chart, wide, id]);

  if (failed) {
    return <p className="text-[13px] leading-relaxed text-ink-2">{label}</p>;
  }

  return (
    <figure className="m-0" role="img" aria-label={label}>
      {svg ? (
        <div className="mermaid-diagram" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <div className="h-56 w-full rounded-sm border border-line bg-paper-2" aria-hidden="true" />
      )}
    </figure>
  );
}
